import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import useIsMobile from '../hooks/useIsMobile';
import ThemeToggle from './ThemeToggle';
import BetaBanner from './BetaBanner';
import CoinIcon from './CoinIcon';
import WalletIcon from './WalletIcon';
import api from '../api';
import {
  FiHome, FiCalendar, FiSearch,
  FiSettings, FiUser, FiLogOut,
  FiActivity, FiFileText, FiMessageCircle, FiImage,
  FiCreditCard, FiStar, FiTruck, FiBell, FiCompass, FiUsers,
  FiTrendingUp, FiAlertTriangle, FiTag, FiInbox,
  FiHelpCircle, FiLock, FiZap, FiShield, FiShare2, FiGift, FiArrowLeft, FiPlusCircle
} from 'react-icons/fi';
import { IoTicketOutline } from 'react-icons/io5';
import { HiOutlineBuildingStorefront } from 'react-icons/hi2';

const VENDOR_NAV_GROUPS = [
  {
    label: 'Main',
    items: [
      { key: 'overview', label: 'Dashboard', icon: <FiActivity size={18} /> },
      { key: 'bookings', label: 'Bookings', icon: <FiFileText size={18} /> },
      { key: 'chats', label: 'Chats', icon: <FiMessageCircle size={18} /> },
      { key: 'portfolio', label: 'Portfolio', icon: <FiImage size={18} /> },
    ]
  },
  {
    label: 'Business',
    items: [
      { key: 'ratecards', label: 'Rate Cards', icon: <FiCreditCard size={18} /> },
      { key: 'calendar', label: 'Calendar', icon: <FiCalendar size={18} /> },
      { key: 'reviews', label: 'Reviews', icon: <FiStar size={18} /> },
    ]
  },
  {
    label: 'Finance',
    items: [
      { key: 'wallet', label: 'Wallet', icon: <WalletIcon size={22} /> },
      { key: 'banking', label: 'Banking', icon: <FiCreditCard size={18} /> },
    ]
  },
  {
    label: 'Operations',
    items: [
      { key: 'deliveries', label: 'Deliveries', icon: <FiTruck size={18} /> },
      { key: 'notifications', label: 'Alerts', icon: <FiBell size={18} />, badge: true },
    ]
  },
  {
    label: 'Network',
    items: [
      { key: 'browse-events', label: 'Find Events', icon: <FiCompass size={18} /> },
      { key: 'community', label: 'Community', icon: <FiUsers size={18} /> },
    ]
  },
  {
    label: 'Account',
    items: [
      { key: 'profile', label: 'Profile', icon: <FiUser size={18} /> },
      { key: 'settings', label: 'Settings', icon: <FiSettings size={18} /> },
    ]
  },
];

const VENDOR_BOTTOM_TABS = [
  { key: 'overview', label: 'Dashboard', icon: <FiActivity size={22} /> },
  { key: 'bookings', label: 'Bookings', icon: <FiFileText size={22} /> },
  { key: 'chats', label: 'Chats', icon: <FiMessageCircle size={22} /> },
  { key: 'wallet', label: 'Wallet', icon: <WalletIcon size={26} /> },
];

const ADMIN_NAV_GROUPS = [
  {
    label: 'Dashboard',
    items: [
      { key: 'overview', label: 'Overview', icon: <FiActivity size={18} /> },
      { key: 'revenue', label: 'Revenue', icon: <FiTrendingUp size={18} /> },
      { key: 'traction', label: 'Traction & Growth', icon: <FiTrendingUp size={18} /> },
    ]
  },
  {
    label: 'Management',
    items: [
      { key: 'vendors', label: 'Vendors', icon: <HiOutlineBuildingStorefront size={18} /> },
      { key: 'users', label: 'Users', icon: <FiUsers size={18} /> },
      { key: 'events', label: 'Events', icon: <FiCalendar size={18} /> },
      { key: 'guests', label: 'Guests & RSVPs', icon: <FiUsers size={18} /> },
      { key: 'staff', label: 'Staff & Roles', icon: <FiShield size={18} /> },
      { key: 'tickets', label: 'Tickets', icon: <FiTag size={18} /> },
      { key: 'uploads', label: 'Uploads', icon: <FiImage size={18} /> },
    ]
  },
  {
    label: 'Financial',
    items: [
      { key: 'invoices', label: 'Invoice CRM', icon: <FiFileText size={18} /> },
      { key: 'vendorRevenue', label: 'Vendor Revenue', icon: <CoinIcon size={18} /> },
      { key: 'contributions', label: 'Contributions', icon: <CoinIcon size={18} /> },
      { key: 'banking', label: 'Banking Review', icon: <FiCreditCard size={18} /> },
      { key: 'payouts', label: 'Payouts', icon: <CoinIcon size={18} /> },
      { key: 'giftCodes', label: 'Gift Codes', icon: <FiCreditCard size={18} /> },
    ]
  },
  {
    label: 'Safety & Support',
    items: [
      { key: 'chats', label: 'Chat Monitor', icon: <FiMessageCircle size={18} /> },
      { key: 'flagged', label: 'Flagged', icon: <FiAlertTriangle size={18} /> },
      { key: 'disputes', label: 'Disputes', icon: <FiAlertTriangle size={18} /> },
      { key: 'enquiries', label: 'Enquiries', icon: <FiInbox size={18} /> },
      { key: 'faqs', label: 'FAQs', icon: <FiHelpCircle size={18} /> },
      { key: 'kyc', label: 'KYC / ID Verification', icon: <FiShield size={18} /> },
    ]
  },
  {
    label: 'Tools',
    items: [
      { key: 'broadcast', label: 'Broadcast', icon: <FiBell size={18} />, badge: true },
      { key: 'reviews', label: 'Reviews', icon: <FiStar size={18} /> },
      { key: 'sponsors', label: 'Sponsors', icon: <FiZap size={18} /> },
      { key: 'aiDesign', label: 'AI Designer', icon: <FiZap size={18} /> },
      { key: 'passwords', label: 'Password Reset', icon: <FiLock size={18} /> },
    ]
  },
];

const ADMIN_BOTTOM_TABS = [
  { key: 'overview', label: 'Dashboard', icon: <FiActivity size={22} /> },
  { key: 'invoices', label: 'Invoices', icon: <FiFileText size={22} /> },
  { key: 'vendors', label: 'Vendors', icon: <HiOutlineBuildingStorefront size={22} /> },
  { key: 'users', label: 'Users', icon: <FiUsers size={22} /> },
  { key: 'staff', label: 'Staff', icon: <FiShield size={22} /> },
];

const EVENT_BOTTOM_TABS = [
  { key: 'overview', label: 'Home', icon: <FiHome size={22} /> },
  { key: 'invitations', label: 'Invites', icon: <FiShare2 size={22} /> },
  { key: 'registry', label: 'Registry', icon: <FiGift size={22} /> },
  { key: 'vendors', label: 'Vendors', icon: <HiOutlineBuildingStorefront size={22} /> },
  { key: 'tickets', label: 'Tickets', icon: <IoTicketOutline size={22} /> },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile] = useIsMobile(768);
  const [profilePanelOpen, setProfilePanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerClosing, setDrawerClosing] = useState(false);
  const touchStartX = useRef(0);
  const drawerRef = useRef(null);
  const closingRef = useRef(false);
  const [latestEventId, setLatestEventId] = useState(null);
  const [toast, setToast] = useState(null);

  // Auto-dismiss toast after 4 seconds
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Fetch latest event for Chats button quick-navigation
  const eventsRef = useRef([]);
  useEffect(() => {
    if (!user) return;
    api.get('/events').then(r => {
      const events = r.data || [];
      eventsRef.current = events;
      if (events.length > 0) setLatestEventId(events[0].id);
    }).catch(() => {});
  }, [user]);

  // Poll for unread notification count (bell badge) + show an in-app toast when a new notification arrives
  const unreadRef = useRef(unreadCount);
  useEffect(() => { unreadRef.current = unreadCount; }, [unreadCount]);
  const [notifToast, setNotifToast] = useState(null);
  const lastNotifIdRef = useRef(0);
  const toastTimerRef = useRef(null);
  const checkNotifications = (list) => {
    if (!list || !list.length) return;
    const newest = list.reduce((a, b) => (a.id > b.id ? a : b), list[0]);
    if (lastNotifIdRef.current === 0) {
      lastNotifIdRef.current = newest.id; // don't toast historical notifications on first load
    } else if (newest.id > lastNotifIdRef.current && !newest.read) {
      lastNotifIdRef.current = newest.id;
      setNotifToast({ id: newest.id, title: newest.title, message: newest.message, link: newest.link });
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => setNotifToast(null), 6000);
    }
    const newUnread = list.filter(n => !n.read).length;
    if (newUnread !== unreadRef.current) {
      setUnreadCount(newUnread);
      try {
        if (navigator.setAppBadge) {
          if (newUnread > 0) navigator.setAppBadge(newUnread);
          else navigator.clearAppBadge();
        }
      } catch {}
    }
  };
  useEffect(() => {
    api.get('/notifications').then(r => checkNotifications(r.data)).catch(() => {});
    const poll = setInterval(() => {
      api.get('/notifications').then(r => checkNotifications(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(poll);
  }, []);

  // Listen for GiGi unread suggestion count
  const [gigiUnread, setGigiUnread] = useState(0);
  useEffect(() => {
    const update = () => {
      try {
        const stored = JSON.parse(localStorage.getItem('gigi_unread') || '[]');
        setGigiUnread(stored.length);
      } catch { setGigiUnread(0); }
    };
    update();
    window.addEventListener('gigi-unread-update', update);
    return () => window.removeEventListener('gigi-unread-update', update);
  }, []);
  // Lock body scroll when drawer is open — useLayoutEffect runs synchronously
  // before paint, preventing the layout-shift jank that useEffect would cause.
  React.useLayoutEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.overscrollBehavior = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.overscrollBehavior = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.overscrollBehavior = '';
    };
  }, [drawerOpen]);

  // ── Close drawer helper (idempotent — safe to call multiple times) ──
  const closeDrawer = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setDrawerClosing(true);
    setTimeout(() => {
      setDrawerOpen(false);
      setDrawerClosing(false);
      closingRef.current = false;
    }, 260);
  };

  const drawerOpenRef = useRef(drawerOpen);
  useEffect(() => { drawerOpenRef.current = drawerOpen; }, [drawerOpen]);

  // Close drawer on navigation with exit animation
  useEffect(() => {
    if (drawerOpenRef.current) {
      const timer = setTimeout(() => closeDrawer(), 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.search]);

  const isVendorPage = location.pathname.startsWith('/vendor-dashboard');
  const isAdminPage = location.pathname.startsWith('/admin');
  const isEventPage = location.pathname.startsWith('/event/');
  const isVendor = user?.role === 'vendor' || user?.role === 'both';
  const isAdmin = !!(user?.is_admin || user?.is_staff);
  const isDark = theme === 'dark';

  // Staff see only the admin sections they were granted (admins see everything)
  const staffPerms = user?.is_staff ? (user?.staff_permissions || []) : null;
  const canAccessTab = (key) => staffPerms ? (staffPerms.includes(key) || key === 'overview') : true;
  const visibleAdminGroups = isAdmin
    ? ADMIN_NAV_GROUPS
        .map(g => ({ ...g, items: g.items.filter(it => canAccessTab(it.key)) }))
        .filter(g => g.items.length > 0)
    : [];
  const visibleAdminBottomTabs = isAdmin
    ? ADMIN_BOTTOM_TABS.filter(t => canAccessTab(t.key))
    : [];


  const searchParams = new URLSearchParams(location.search);
  const vendorActiveTab = searchParams.get('tab') || 'overview';
  const adminTab = searchParams.get('tab') || 'overview';
  const eventTab = searchParams.get('tab') || 'overview';

  const goVendorTab = (key) => {
    navigate(`/vendor-dashboard?tab=${key}`);
  };

  const goAdminTab = (key) => {
    navigate(`/admin?tab=${key}`);
  };

  // ── Host nav ──
  const hostNavLinks = [
    { label: 'Home', icon: <FiHome size={20} />, path: '/dashboard' },
    { label: 'Marketplace', icon: <FiSearch size={20} />, path: '/vendors' },
    { label: 'Tickets', icon: <FiTag size={20} />, onClick: () => { if (isEventPage) { navigate(`/event/${eventId}?tab=tickets`); } else if (latestEventId) { navigate(`/event/${latestEventId}?tab=tickets`); } else { setToast({ message: 'Create an event first to start selling tickets.', action: { label: 'Create Event', onClick: () => { navigate('/dashboard?new-event=1'); } } }); } } },
    { label: 'Chats', icon: <FiMessageCircle size={20} />, onClick: () => { if (isEventPage) { navigate(`/event/${eventId}?tab=vendors`); } else if (latestEventId) { navigate(`/event/${latestEventId}?tab=vendors`); } else { setToast({ message: 'Create an event first so vendors know what you\'re hosting.', action: { label: 'Create Event', onClick: () => { navigate('/dashboard?new-event=1'); } } }); } } },
    { label: 'Wallet', icon: <WalletIcon size={20} />, path: '/wallet' },
  ];

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // ── NAV ITEM RENDERER (shared) ──
  const renderNavItem = (item, active, onClick, extra) => {
    const Tag = item.path ? Link : 'button';
    const handleClick = item.onClick ? (e) => { item.onClick(e); if (onClick) onClick(e); } : onClick;
    const tagProps = item.path
      ? { to: item.path, onClick: handleClick }
      : { onClick: handleClick };
    return (
      <Tag
        key={item.key || item.path}
        {...tagProps}
        style={{
          display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', width: '100%',
          borderRadius: '10px', fontSize: '15px', fontWeight: active ? 600 : 400,
          color: active ? 'var(--rose)' : 'var(--dark)',
          background: active ? (isDark ? 'rgba(227,66,92,0.1)' : 'var(--rose-light)') : 'transparent',
          textDecoration: 'none', transition: 'all 0.2s', marginBottom: '2px',
          border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
          ...(extra || {}),
        }}
      >
        {item.icon}
        {item.label}
      </Tag>
    );
  };

  // ═══════════════════════════════════════════
  // ── HOST SIDEBAR ──
  // ═══════════════════════════════════════════
  const renderHostSidebar = () => (
    <aside style={{
      width: '225px', minWidth: '225px', height: '100vh', position: 'fixed', top: 0, left: 0,
      display: 'flex', flexDirection: 'column', background: 'var(--white)',
      borderRight: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
      zIndex: 90, overflowY: 'auto',
    }}>
      <div style={{ padding: '20px 16px 8px' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
          <img src="/ggr-logo-deck.png" alt="GGR" style={{ height: isEventPage ? '36px' : '48px' }} />
        </Link>
      </div>
      <nav style={{ flex: 1, padding: '12px 10px' }}>
        {hostNavLinks.map(link => renderNavItem(
          { ...link, key: link.path || link.label }, link.path ? isActive(link.path) : (isEventPage && link.label === 'Tickets' && location.search.includes('tab=tickets'))
        ))}
        {renderNavItem({ key: 'settings', label: 'Settings', icon: <FiSettings size={20} /> }, false, () => location.pathname === '/dashboard' ? window.dispatchEvent(new CustomEvent('settings-toggle')) : navigate('/dashboard', { state: { openSettings: true } }))}
        <div style={{ padding: '0 6px', marginTop: '12px' }}>
          <button
            onClick={() => navigate('/upgrade?type=host')}
            className="upgrade-premium"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '12px 16px', width: '100%', borderRadius: '12px', border: 'none',
              cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 600,
              background: 'linear-gradient(135deg, #E3425C, #d13a4f)',
              color: '#fff',
              boxShadow: '0 4px 14px rgba(227,66,92,0.35)',
            }}
          >
            <FiZap size={16} /> Upgrade to Premium
          </button>
        </div>
      </nav>
      {(isVendor || isAdmin) && (
        <div style={{ padding: '0 12px', marginBottom: '8px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--gray)', padding: '0 16px', marginBottom: '6px' }}>More</div>
          {isVendor && renderNavItem({ key: 'vendor-dash', path: '/vendor-dashboard', label: 'Vendor Dashboard', icon: <FiHome size={18} /> }, isActive('/vendor-dashboard'), undefined, { color: '#d4a853', background: isActive('/vendor-dashboard') ? 'rgba(212,168,83,0.12)' : 'transparent' })}
          {isAdmin && renderNavItem({ key: 'admin', path: '/admin', label: 'Admin Panel', icon: <FiSettings size={18} /> }, isActive('/admin'))}
        </div>
      )}
      <div style={{ padding: '12px', borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', marginBottom: '8px' }}>
          <button
            className="tab-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))}
            aria-label="Notifications"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: isDark ? '#fff' : 'var(--dark)', padding: 0, flexShrink: 0, position: 'relative' }}
          >
            <FiBell size={16} />
            {unreadCount > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-4px', minWidth: '18px', height: '18px', borderRadius: '9px', background: '#E3425C', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, fontFamily: 'Inter, sans-serif' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
          </button>
          <ThemeToggle />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || 'User'}</div>
          </div>
        </div>
        <Link to="/terms" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}><FiFileText size={14} /> Terms</Link>
        <Link to="/privacy" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}><FiShield size={14} /> Privacy</Link>
        <Link to="/partnerships" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s', marginBottom: '4px' }}><FiZap size={14} /> Sponsorship</Link>
        <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', width: '100%', borderRadius: '10px', fontSize: '14px', fontWeight: 500, border: 'none', color: 'var(--gray)', background: 'transparent', cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'Inter, sans-serif' }}>
          <FiLogOut size={18} /> Logout
        </button>
      </div>
    </aside>
  );

  // ═══════════════════════════════════════════
  // ── EVENT SIDEBAR (desktop) ──
  // ═══════════════════════════════════════════
  const eventId = isEventPage ? location.pathname.split('/')[2] : null;

  const renderEventSidebar = () => (
    <aside style={{
      width: '225px', minWidth: '225px', height: '100vh', position: 'fixed', top: 0, left: 0,
      display: 'flex', flexDirection: 'column', background: isDark ? '#1c1c1e' : 'var(--white)',
      borderRight: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
      zIndex: 90, overflowY: 'auto',
    }}>
      <div style={{ padding: '20px 16px 8px' }}>
        <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img src="/ggr-logo-deck.png" alt="GGR" style={{ height: '36px' }} />
        </Link>
      </div>
      <nav style={{ flex: 1, padding: '12px 10px' }}>
        {EVENT_BOTTOM_TABS.map(tab => {
          const active = eventTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => {
                if (tab.key === 'overview') {
                  navigate(location.pathname);
                } else {
                  navigate(`${location.pathname}?tab=${tab.key}`);
                }
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', width: '100%',
                borderRadius: '10px', fontSize: '15px', fontWeight: active ? 600 : 400,
                color: active ? '#E3425C' : (isDark ? '#c0c0c4' : 'var(--dark)'),
                background: active ? 'rgba(227,66,92,0.1)' : 'transparent',
                border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                transition: 'all 0.2s', marginBottom: '2px',
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          );
        })}
      </nav>
      <Link
        to="/dashboard"
        style={{
          display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px',
          margin: '0 12px 4px', borderRadius: '10px', fontSize: '14px', fontWeight: 500,
          color: 'var(--gray)', textDecoration: 'none', transition: 'all 0.2s',
        }}
      >
        <FiHome size={18} /> Back to Dashboard
      </Link>
      <div style={{ padding: '12px', borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', marginBottom: '8px' }}>
          <button
            className="tab-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))}
            aria-label="Notifications"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: isDark ? '#fff' : 'var(--dark)', padding: 0, flexShrink: 0, position: 'relative' }}
          >
            <FiBell size={16} />
            {unreadCount > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-4px', minWidth: '18px', height: '18px', borderRadius: '9px', background: '#E3425C', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, fontFamily: 'Inter, sans-serif' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
          </button>
          <ThemeToggle />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: isDark ? '#e8e8ea' : 'var(--dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || 'User'}</div>
          </div>
        </div>
        <Link to="/terms" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}><FiFileText size={14} /> Terms</Link>
        <Link to="/privacy" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}><FiShield size={14} /> Privacy</Link>
        <Link to="/partnerships" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s', marginBottom: '4px' }}><FiZap size={14} /> Sponsorship</Link>
        <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', width: '100%', borderRadius: '10px', fontSize: '14px', fontWeight: 500, border: 'none', color: 'var(--gray)', background: 'transparent', cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'Inter, sans-serif' }}>
          <FiLogOut size={18} /> Logout
        </button>
      </div>
    </aside>
  );

  // ═══════════════════════════════════════════
  // ── VENDOR SIDEBAR ──
  // ═══════════════════════════════════════════
  const renderVendorSidebar = () => (
    <aside style={{
      width: '225px', minWidth: '225px', height: '100vh', position: 'fixed', top: 0, left: 0,
      display: 'flex', flexDirection: 'column', background: 'var(--white)',
      borderRight: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
      zIndex: 90, overflowY: 'auto',
    }}>
      <div style={{ padding: '20px 16px 8px' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
          <img src="/ggr-logo-deck.png" alt="GGR" style={{ height: '48px' }} />
        </Link>
      </div>
      <nav style={{ flex: 1, padding: '4px 10px' }}>
        {VENDOR_NAV_GROUPS.map(group => (
          <div key={group.label} style={{ marginBottom: '4px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--gray)', padding: '8px 16px 4px' }}>{group.label}</div>
            {group.items.map(item => {
              const active = vendorActiveTab === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => goVendorTab(item.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', width: '100%',
                    borderRadius: '10px', fontSize: '15px', fontWeight: active ? 600 : 400,
                    color: active ? 'var(--rose)' : 'var(--dark)',
                    background: active ? (isDark ? 'rgba(227,66,92,0.1)' : 'var(--rose-light)') : 'transparent',
                    border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                    transition: 'all 0.2s', marginBottom: '1px',
                  }}
                >
                  {item.badge ? <span style={{ position: 'relative', display: 'inline-flex' }}>{item.icon}{unreadCount > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-4px', minWidth: '18px', height: '18px', borderRadius: '9px', background: '#E3425C', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, fontFamily: 'Inter, sans-serif' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>}</span> : item.icon}
                  {item.label}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
      <div style={{ padding: '0 12px', marginBottom: '8px' }}>
        <button
          onClick={() => navigate('/upgrade?type=vendor')}
          className="upgrade-premium"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            padding: '12px 16px', width: '100%', borderRadius: '12px', border: 'none',
            cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 600,
            background: 'linear-gradient(135deg, #d4a853, #b8913a)',
            color: '#fff',
            boxShadow: '0 4px 14px rgba(212,168,83,0.4)',
          }}
        >
          <FiZap size={16} /> Upgrade to Premium
        </button>
      </div>
      {/* Back to host dashboard if dual-role */}
      {user?.role === 'both' && (
        <div style={{ padding: '0 12px', marginBottom: '4px' }}>
          <Link
            to="/dashboard"
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px',
              borderRadius: '10px', fontSize: '14px', fontWeight: 500,
              color: '#E3425C', textDecoration: 'none', transition: 'all 0.2s',
            }}
          >
            <FiUser size={18} />
            Host Dashboard
          </Link>
        </div>
      )}
      <div style={{ padding: '12px', borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', marginBottom: '8px' }}>
          <button
            className="tab-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))}
            aria-label="Notifications"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: isDark ? '#fff' : 'var(--dark)', padding: 0, flexShrink: 0, position: 'relative' }}
          >
            <FiBell size={16} />
            {unreadCount > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-4px', minWidth: '18px', height: '18px', borderRadius: '9px', background: '#E3425C', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, fontFamily: 'Inter, sans-serif' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
          </button>
          <ThemeToggle />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || 'User'}</div>
          </div>
        </div>
        <Link to="/terms" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}><FiFileText size={14} /> Terms</Link>
        <Link to="/privacy" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}><FiShield size={14} /> Privacy</Link>
        <Link to="/partnerships" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s', marginBottom: '4px' }}><FiZap size={14} /> Sponsorship</Link>
        <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', width: '100%', borderRadius: '10px', fontSize: '14px', fontWeight: 500, border: 'none', color: 'var(--gray)', background: 'transparent', cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'Inter, sans-serif' }}>
          <FiLogOut size={18} /> Logout
        </button>
      </div>
    </aside>
  );

  // ═══════════════════════════════════════════
  // ── ADMIN SIDEBAR ──
  // ═══════════════════════════════════════════
  const renderAdminSidebar = () => (
    <aside style={{
      width: '225px', minWidth: '225px', height: '100vh', position: 'fixed', top: 0, left: 0,
      display: 'flex', flexDirection: 'column', background: 'var(--white)',
      borderRight: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
      zIndex: 90, overflowY: 'auto',
    }}>
      <div style={{ padding: '20px 16px 8px' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
          <img src="/ggr-logo-deck.png" alt="GGR" style={{ height: '48px' }} />
        </Link>
        <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', color: 'var(--rose)', marginTop: '4px', paddingLeft: '4px' }}>Admin Panel</div>
      </div>
      <nav style={{ flex: 1, padding: '4px 10px' }}>
        {visibleAdminGroups.map(group => (
          <div key={group.label} style={{ marginBottom: '4px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--gray)', padding: '8px 16px 4px' }}>{group.label}</div>
            {group.items.map(item => {
              const active = adminTab === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => goAdminTab(item.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', width: '100%',
                    borderRadius: '10px', fontSize: '15px', fontWeight: active ? 600 : 400,
                    color: active ? 'var(--rose)' : 'var(--dark)',
                    background: active ? (isDark ? 'rgba(227,66,92,0.1)' : 'var(--rose-light)') : 'transparent',
                    border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                    transition: 'all 0.2s', marginBottom: '1px',
                  }}
                >
                  {item.badge ? <span style={{ position: 'relative', display: 'inline-flex' }}>{item.icon}{unreadCount > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-4px', minWidth: '18px', height: '18px', borderRadius: '9px', background: '#E3425C', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, fontFamily: 'Inter, sans-serif' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>}</span> : item.icon}
                  {item.label}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
      {/* Back to host dashboard */}
      {isVendor && (
        <div style={{ padding: '0 12px', marginBottom: '4px' }}>
          <Link to="/vendor-dashboard" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: 500, color: '#d4a853', textDecoration: 'none' }}>
            <FiHome size={18} /> Vendor Dashboard
          </Link>
        </div>
      )}
      <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', margin: '0 12px 4px', borderRadius: '10px', fontSize: '14px', fontWeight: 500, color: '#E3425C', textDecoration: 'none' }}>
        <FiUser size={18} /> Host Dashboard
      </Link>
      <div style={{ padding: '12px', borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.04)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', marginBottom: '8px' }}>
          <button
            className="tab-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))}
            aria-label="Notifications"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: isDark ? '#fff' : 'var(--dark)', padding: 0, flexShrink: 0, position: 'relative' }}
          >
            <FiBell size={16} />
            {unreadCount > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-4px', minWidth: '18px', height: '18px', borderRadius: '9px', background: '#E3425C', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, fontFamily: 'Inter, sans-serif' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
          </button>
          <ThemeToggle />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || 'User'}</div>
          </div>
        </div>
        <Link to="/terms" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}><FiFileText size={14} /> Terms</Link>
        <Link to="/privacy" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s' }}><FiShield size={14} /> Privacy</Link>
        <Link to="/partnerships" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s', marginBottom: '4px' }}><FiZap size={14} /> Sponsorship</Link>
        <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', width: '100%', borderRadius: '10px', fontSize: '14px', fontWeight: 500, border: 'none', color: 'var(--gray)', background: 'transparent', cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'Inter, sans-serif' }}>
          <FiLogOut size={18} /> Logout
        </button>
      </div>
    </aside>
  );

  // ── Desktop Sidebar (switches by page) ──
  const renderSidebar = () => isEventPage ? renderEventSidebar() : (isAdminPage ? renderAdminSidebar() : (isVendorPage ? renderVendorSidebar() : renderHostSidebar()));

  // ═══════════════════════════════════════════
  // ── DRAWER HANDLE (mobile floating pill) ──
  // ═══════════════════════════════════════════
  const renderDrawerHandle = () => (
    <button
      onClick={() => setDrawerOpen(true)}
      aria-label="Open navigation drawer"
      style={{
        position: 'fixed', left: 0, top: '48%', transform: 'translateY(-50%)',
        zIndex: 160, width: '17px', height: '80px',
        borderRadius: '0 999px 999px 0', border: 'none', cursor: 'pointer',
        background: isVendorPage ? '#d4a853' : '#E3425C',
        boxShadow: isVendorPage ? '0 4px 12px rgba(212,168,83,0.45)' : '0 4px 12px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: '4px', padding: 0,
        transition: 'transform 0.15s ease', WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{ width: '9px', height: '2px', borderRadius: '1px', background: '#fff' }} />
      <span style={{ width: '9px', height: '2px', borderRadius: '1px', background: '#fff' }} />
      <span style={{ width: '9px', height: '2px', borderRadius: '1px', background: '#fff' }} />
      {unreadCount > 0 && (
        <span style={{
          position: 'absolute', top: '-6px', right: '-6px',
          minWidth: '14px', height: '14px', borderRadius: '7px',
          background: '#fff', color: isVendorPage ? '#b8913a' : '#E3425C', fontSize: '8px', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 2px', lineHeight: 1, fontFamily: 'Inter, sans-serif',
          border: `1.5px solid ${isVendorPage ? '#d4a853' : '#E3425C'}`,
        }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
      )}
    </button>
  );

  // ═══════════════════════════════════════════
  // ── DRAWER PANEL (mobile glass drawer) ──
  // ═══════════════════════════════════════════
  const renderDrawer = () => {
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
      if (touchStartX.current - e.changedTouches[0].clientX > 60) closeDrawer();
    };

    // Navigation links based on page
    const navItems = isEventPage ? (
      <>
        <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--gray)', padding: '0 16px 6px', marginTop: '8px' }}>Event</div>
        {EVENT_BOTTOM_TABS.map(tab => {
          const active = eventTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => { closeDrawer(); if (tab.key === 'overview') navigate(location.pathname); else navigate(`${location.pathname}?tab=${tab.key}`); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', width: '100%',
                borderRadius: '10px', fontSize: '15px', fontWeight: active ? 600 : 400,
                color: active ? '#E3425C' : (isDark ? '#c0c0c4' : 'var(--dark)'),
                background: active ? 'rgba(227,66,92,0.1)' : 'transparent',
                border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                transition: 'all 0.2s', marginBottom: '2px',
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          );
        })}
        <Link to="/dashboard" onClick={closeDrawer} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: 500, color: 'var(--gray)', textDecoration: 'none' }}>
          <FiHome size={18} /> Back to Dashboard
        </Link>
      </>
    ) : isAdminPage ? (
      <>
        {visibleAdminGroups.map(group => (
          <div key={group.label} style={{ marginBottom: '4px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--gray)', padding: '8px 16px 4px' }}>{group.label}</div>
            {group.items.map(item => {
              const active = adminTab === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => { closeDrawer(); goAdminTab(item.key); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', width: '100%',
                    borderRadius: '10px', fontSize: '15px', fontWeight: active ? 600 : 400,
                    color: active ? 'var(--rose)' : 'var(--dark)',
                    background: active ? (isDark ? 'rgba(227,66,92,0.1)' : 'var(--rose-light)') : 'transparent',
                    border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                    transition: 'all 0.2s', marginBottom: '1px',
                  }}
                >
                  {item.badge ? <span style={{ position: 'relative', display: 'inline-flex' }}>{item.icon}{unreadCount > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-4px', minWidth: '18px', height: '18px', borderRadius: '9px', background: '#E3425C', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, fontFamily: 'Inter, sans-serif' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>}</span> : item.icon}
                  {item.label}
                </button>
              );
            })}
          </div>
        ))}
        {isVendor && (
          <Link to="/vendor-dashboard" onClick={closeDrawer} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: 500, color: '#d4a853', textDecoration: 'none' }}>
            <FiHome size={18} /> Vendor Dashboard
          </Link>
        )}
        <Link to="/dashboard" onClick={closeDrawer} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: 500, color: '#E3425C', textDecoration: 'none' }}>
          <FiUser size={18} /> Host Dashboard
        </Link>
      </>
    ) : isVendorPage ? (
      <>
        {VENDOR_NAV_GROUPS.map(group => (
          <div key={group.label} style={{ marginBottom: '4px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--gray)', padding: '8px 16px 4px' }}>{group.label}</div>
            {group.items.map(item => {
              const active = vendorActiveTab === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => { closeDrawer(); goVendorTab(item.key); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', width: '100%',
                    borderRadius: '10px', fontSize: '15px', fontWeight: active ? 600 : 400,
                    color: active ? '#b8913a' : 'var(--dark)',
                    background: active ? 'rgba(212,168,83,0.12)' : 'transparent',
                    border: 'none', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                    transition: 'all 0.2s', marginBottom: '1px',
                  }}
                >
                  {item.badge ? <span style={{ position: 'relative', display: 'inline-flex' }}>{item.icon}{unreadCount > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-4px', minWidth: '18px', height: '18px', borderRadius: '9px', background: '#E3425C', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, fontFamily: 'Inter, sans-serif' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>}</span> : item.icon}
                  {item.label}
                </button>
              );
            })}
          </div>
        ))}
        {user?.role === 'both' && (
          <Link to="/dashboard" onClick={closeDrawer} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: 500, color: '#E3425C', textDecoration: 'none' }}>
            <FiUser size={18} /> Host Dashboard
          </Link>
        )}
      </>
    ) : (
      <>
        {hostNavLinks.map(link => renderNavItem(
          { ...link, key: link.path || link.label }, link.path ? isActive(link.path) : (isEventPage && link.label === 'Tickets' && location.search.includes('tab=tickets')), closeDrawer
        ))}
        {renderNavItem({ key: 'settings', label: 'Settings', icon: <FiSettings size={20} /> }, false, () => { closeDrawer(); if (location.pathname === '/dashboard') { window.dispatchEvent(new CustomEvent('settings-toggle')); } else { navigate('/dashboard', { state: { openSettings: true } }); } })}
        {(isVendor || isAdmin) && (
          <div style={{ marginTop: '4px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--gray)', padding: '8px 16px 4px' }}>More</div>
            {isVendor && renderNavItem({ key: 'vendor-dash', path: '/vendor-dashboard', label: 'Vendor Dashboard', icon: <FiHome size={18} /> }, isActive('/vendor-dashboard'), closeDrawer, { color: '#d4a853', background: isActive('/vendor-dashboard') ? 'rgba(212,168,83,0.12)' : 'transparent' })}
            {isAdmin && renderNavItem({ key: 'admin', path: '/admin', label: 'Admin Panel', icon: <FiSettings size={18} /> }, isActive('/admin'), closeDrawer)}
          </div>
        )}
      </>
    );

    return (
      <aside
        ref={drawerRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          position: 'fixed', top: 0, left: 0, height: '100dvh', zIndex: 310,
          width: '72vw', overflowY: 'auto',
          background: isDark ? 'rgba(28,28,30,0.98)' : 'rgba(255,255,255,0.98)',
          borderRadius: '0 32px 32px 0',
          boxShadow: 'var(--shadow-xl)',
          borderRight: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.04)',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          display: 'flex', flexDirection: 'column',
          WebkitOverflowScrolling: 'touch',
          ...(drawerOpen || drawerClosing ? {
            visibility: 'visible',
            pointerEvents: 'auto',
            willChange: 'transform',
            animation: drawerClosing
              ? 'drawerSlideOut 260ms cubic-bezier(0.16,1,0.3,1) forwards'
              : 'drawerSlideIn 240ms cubic-bezier(0.16,1,0.3,1) forwards',
          } : {
            visibility: 'hidden',
            pointerEvents: 'none',
            transform: 'translateX(-100%)',
          }),
        }}
      >
        {/* User greeting */}
        <div style={{ padding: '24px 20px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: isVendorPage ? 'rgba(212,168,83,0.15)' : 'var(--rose-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 700, color: isVendorPage ? '#b8913a' : 'var(--rose)', flexShrink: 0 }}>
              {(user?.name || 'U')[0].toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '16px', fontWeight: 600, color: isDark ? '#e8e8ea' : 'var(--dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || 'User'}</div>
              <div style={{ fontSize: '13px', color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email || ''}</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '8px 12px' }}>
          {navItems}
        </nav>

        {/* Bottom actions */}
        <div style={{ padding: '12px', borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.04)' }}>
          {/* Upgrade Premium */}
          <button
            onClick={() => { closeDrawer(); navigate(isVendor ? '/upgrade?type=vendor' : '/upgrade?type=host'); }}
            className="upgrade-premium"
            style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px',
              width: '100%', borderRadius: '12px', border: 'none', cursor: 'pointer',
              background: isVendorPage ? 'linear-gradient(135deg, #d4a853, #b8913a)' : 'linear-gradient(135deg, #E3425C, #d13a4f)',
              color: '#fff', fontSize: '15px', fontWeight: 600,
              fontFamily: 'Inter, sans-serif', marginBottom: '8px',
              boxShadow: isVendorPage ? '0 4px 14px rgba(212,168,83,0.4)' : '0 4px 14px rgba(227,66,92,0.35)',
            }}
          >
            <FiZap size={18} /> Upgrade to Premium
          </button>

          {/* Help Centre */}
          <Link to="/help" onClick={closeDrawer} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', width: '100%', borderRadius: '10px', fontSize: '14px', fontWeight: 500, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif' }}>
            <FiHelpCircle size={18} /> Help Centre
          </Link>

          {/* Footer links */}
          <Link to="/terms" onClick={closeDrawer} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif' }}><FiFileText size={14} /> Terms</Link>
          <Link to="/privacy" onClick={closeDrawer} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif' }}><FiShield size={14} /> Privacy</Link>
          <Link to="/partnerships" onClick={closeDrawer} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', width: '100%', borderRadius: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray)', textDecoration: 'none', fontFamily: 'Inter, sans-serif', marginBottom: '4px' }}><FiZap size={14} /> Sponsorship</Link>

          {/* Logout */}
          <button onClick={() => { closeDrawer(); handleLogout(); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', width: '100%', borderRadius: '10px', fontSize: '14px', fontWeight: 500, border: 'none', color: 'var(--red)', background: 'transparent', cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
            <FiLogOut size={18} /> Logout
          </button>
        </div>
      </aside>
    );
  };

  // ── Mobile Header (shared — varies by page) ──
  const renderMobileHeader = () => isEventPage ? (
    <div className="nav-blur" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'env(safe-area-inset-top, 0px) 16px 0', height: `calc(48px + env(safe-area-inset-top, 0px))`, background: isDark ? 'rgba(20,20,22,0.92)' : 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' }}>
      <img src="/ggr-logo-deck.png" alt="GGR" style={{ height: '28px' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          className="tab-btn"
          onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))}
          aria-label="Notifications"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: isDark ? '#fff' : 'var(--dark)', position: 'relative' }}
        >
          <FiBell size={18} />
          {unreadCount > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-4px', minWidth: '18px', height: '18px', borderRadius: '9px', background: '#E3425C', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, fontFamily: 'Inter, sans-serif' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
        </button>
        <button
          className="tab-btn"
          onClick={() => location.pathname === '/dashboard' ? window.dispatchEvent(new CustomEvent('settings-toggle')) : navigate('/dashboard', { state: { openSettings: true } })}
          aria-label="Settings"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: isDark ? '#fff' : 'var(--dark)' }}
        >
          <FiSettings size={18} />
        </button>
        <button
          className="tab-btn"
          onClick={handleLogout}
          aria-label="Logout"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: isDark ? '#fff' : 'var(--dark)' }}
        >
          <FiLogOut size={18} />
        </button>
        <ThemeToggle />
      </div>
    </div>
  ) : (
    <div className="nav-blur" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'env(safe-area-inset-top, 0px) 16px 0', height: `calc(56px + env(safe-area-inset-top, 0px))`, background: isDark ? 'rgba(20,20,22,0.92)' : 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' }}>
      <img src="/ggr-logo-deck.png" alt="GGR" style={{ height: '32px' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          className="tab-btn"
          onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))}
          aria-label="Notifications"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: isDark ? '#fff' : 'var(--dark)', position: 'relative' }}
        >
          <FiBell size={18} />
          {unreadCount > 0 && <span style={{ position: 'absolute', top: '-2px', right: '-4px', minWidth: '18px', height: '18px', borderRadius: '9px', background: '#E3425C', color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', lineHeight: 1, fontFamily: 'Inter, sans-serif' }}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
        </button>
        <button
          className="tab-btn"
          onClick={() => location.pathname === '/dashboard' ? window.dispatchEvent(new CustomEvent('settings-toggle')) : navigate('/dashboard', { state: { openSettings: true } })}
          aria-label="Settings"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: isDark ? '#fff' : 'var(--dark)' }}
        >
          <FiSettings size={18} />
        </button>
        <button
          className="tab-btn"
          onClick={handleLogout}
          aria-label="Logout"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', color: isDark ? '#fff' : 'var(--dark)' }}
        >
          <FiLogOut size={18} />
        </button>
        <ThemeToggle />
      </div>
    </div>
  );

  // ═══════════════════════════════════════════
  // ── HOST BOTTOM TABS (⚠️ LOCKED — DO NOT REMOVE) ──
  // ═══════════════════════════════════════════
  const renderHostBottomTabs = () => (
    <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-around', background: isDark ? 'rgba(28,28,30,0.80)' : 'rgba(255,255,255,0.72)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderTop: isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(255,255,255,0.5)', boxShadow: '0 -8px 32px rgba(0,0,0,0.10)', zIndex: 150, height: '64px', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <TabButton icon={<HiOutlineBuildingStorefront size={22} />} label="Vendors" active={location.pathname.startsWith('/vendors')} onClick={() => navigate('/vendors')} isDark={isDark} />
      <TabButton icon={<FiTag size={22} />} label="Tickets" active={isEventPage && location.search.includes('tab=tickets')} onClick={() => { if (isEventPage) { navigate(`/event/${eventId}?tab=tickets`); } else if (latestEventId) { navigate(`/event/${latestEventId}?tab=tickets`); } else { setToast({ message: 'Create an event first to start selling tickets.', action: { label: 'Create Event', onClick: () => { navigate('/dashboard?new-event=1'); } } }); } }} isDark={isDark} />
      {/* Center Event button — larger, raised, branded */}
      <button
        className="tab-btn"
        onClick={() => { if (location.pathname === '/dashboard') { window.dispatchEvent(new CustomEvent('create-event-toggle')); } else { navigate('/dashboard', { state: { openCreate: true } }); } }}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
          padding: 0, border: 'none', background: 'transparent',
          cursor: 'pointer', fontFamily: 'Inter, sans-serif',
          minWidth: '56px', position: 'relative',
        }}
      >
        <div style={{
          width: '44px', height: '44px', borderRadius: '50%',
          background: 'radial-gradient(circle at 40% 35%, #f27d8c, #d13a4f)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 3px 10px rgba(227,66,92,0.45)',
          marginTop: '-18px',
        }}>
          <FiPlusCircle size={24} color="#fff" />
        </div>
        <span style={{ fontSize: '10px', fontWeight: 500, color: 'var(--rose)', marginTop: '1px' }}>Event</span>
      </button>
      <TabButton icon={<WalletIcon size={26} />} label="Wallet" active={location.pathname === '/wallet'} onClick={() => navigate('/wallet')} isDark={isDark} />
      <TabButton icon={<FiUser size={22} />} label="Profile" active={profilePanelOpen} onClick={() => setProfilePanelOpen(prev => !prev)} isDark={isDark} />
    </nav>
  );

  // ═══════════════════════════════════════════
  // ── VENDOR BOTTOM TABS (⚠️ LOCKED — DO NOT REMOVE) ──
  // ═══════════════════════════════════════════
  const renderVendorBottomTabs = () => (
    <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-around', background: isDark ? 'rgba(28,28,30,0.80)' : 'rgba(255,255,255,0.72)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderTop: isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(255,255,255,0.5)', boxShadow: '0 -8px 32px rgba(0,0,0,0.10)', zIndex: 150, height: '64px', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {VENDOR_BOTTOM_TABS.map(tab => (
        <TabButton
          key={tab.key}
          icon={tab.icon}
          label={tab.label}
          active={vendorActiveTab === tab.key}
          onClick={() => goVendorTab(tab.key)}
          isDark={isDark}
        />
      ))}
      <TabButton icon={<FiUser size={22} />} label="Profile" active={profilePanelOpen} onClick={() => setProfilePanelOpen(prev => !prev)} isDark={isDark} />
    </nav>
  );

  // ═══════════════════════════════════════════
  // ── ADMIN BOTTOM TABS (⚠️ LOCKED — DO NOT REMOVE) ──
  // ═══════════════════════════════════════════
  const renderAdminBottomTabs = () => (
    <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-around', background: isDark ? 'rgba(28,28,30,0.80)' : 'rgba(255,255,255,0.72)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderTop: isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(255,255,255,0.5)', boxShadow: '0 -8px 32px rgba(0,0,0,0.10)', zIndex: 150, height: '64px', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {visibleAdminBottomTabs.map(tab => (
        <TabButton
          key={tab.key}
          icon={tab.icon}
          label={tab.label}
          active={adminTab === tab.key}
          onClick={() => goAdminTab(tab.key)}
          isDark={isDark}
        />
      ))}
      <TabButton icon={<FiUser size={22} />} label="Profile" active={profilePanelOpen} onClick={() => setProfilePanelOpen(prev => !prev)} isDark={isDark} />
    </nav>
  );

  // ═══════════════════════════════════════════
  // ── EVENT BOTTOM TABS (⚠️ LOCKED — DO NOT REMOVE) ──
  // ═══════════════════════════════════════════
  const renderEventBottomTabs = () => {
    const goEventTab = (key) => {
      if (key === 'overview') {
        navigate(location.pathname);
      } else {
        navigate(`${location.pathname}?tab=${key}`);
      }
    };
    return (
      <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-around', background: isDark ? 'rgba(28,28,30,0.80)' : 'rgba(255,255,255,0.72)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderTop: isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(255,255,255,0.5)', boxShadow: '0 -8px 32px rgba(0,0,0,0.10)', zIndex: 150, height: '64px', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {EVENT_BOTTOM_TABS.map(tab => (
          <TabButton
            key={tab.key}
            icon={tab.icon}
            label={tab.label}
            active={eventTab === tab.key}
            onClick={() => goEventTab(tab.key)}
            isDark={isDark}
          />
        ))}
        <TabButton icon={<FiUser size={22} />} label="Profile" active={profilePanelOpen} onClick={() => setProfilePanelOpen(prev => !prev)} isDark={isDark} />
      </nav>
    );
  };

  /* ⚠️ LOCKED — DO NOT REMOVE — Mobile bottom tab navigation is the primary footer for PWA */
  const renderBottomTabs = () => isEventPage ? renderEventBottomTabs() : (isAdminPage ? renderAdminBottomTabs() : (isVendorPage ? renderVendorBottomTabs() : renderHostBottomTabs()));

  // ═══════════════════════════════════════════
  // ── PROFILE PANEL (mobile bottom sheet) ──
  // ═══════════════════════════════════════════
  const renderProfilePanel = () => (
    <>
      {profilePanelOpen && (
        <div onClick={() => setProfilePanelOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', zIndex: 200 }} />
      )}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'var(--white)', zIndex: 201, borderRadius: '20px 20px 0 0', padding: '8px 0 calc(24px + env(safe-area-inset-bottom, 0px))', transform: profilePanelOpen ? 'translateY(0)' : 'translateY(100%)', transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1)', boxShadow: '0 -4px 24px rgba(0,0,0,0.15), 0 -1px 4px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '0 20px 0 16px' }}>
          <button
            onClick={() => { navigate(isVendorPage ? '/vendor-dashboard' : '/dashboard'); setProfilePanelOpen(false); }}
            style={{ width: '36px', height: '36px', borderRadius: '50%', border: 'none', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--dark)', flexShrink: 0, position: 'relative' }}
            aria-label="Back to Dashboard"
          >
            <FiArrowLeft size={18} />
          </button>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)' }} />
          </div>
          <div style={{ width: '36px' }} />
        </div>
        <div style={{ padding: '0 20px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--gray)', marginBottom: '12px', marginTop: '4px' }}>Account</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0 16px', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)', marginBottom: '8px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--rose-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 700, color: 'var(--rose)' }}>
              {(user?.name || 'U')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--dark)' }}>{user?.name || 'User'}</div>
              <div style={{ fontSize: '13px', color: 'var(--gray)' }}>{user?.email || ''}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 4px', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)' }}>
            <span style={{ fontSize: '15px', fontWeight: 500, color: 'var(--dark)', fontFamily: 'Inter, sans-serif' }}>Theme</span>
            <ThemeToggle />
          </div>
          {isVendorPage ? (
            <button onClick={() => { goVendorTab('settings'); setProfilePanelOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '15px', fontWeight: 500, color: 'var(--dark)', fontFamily: 'Inter, sans-serif', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)' }}>
              <FiSettings size={18} /> Settings
            </button>
          ) : isEventPage ? (
            <button onClick={() => { navigate('/dashboard'); setProfilePanelOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '15px', fontWeight: 500, color: 'var(--dark)', fontFamily: 'Inter, sans-serif', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)' }}>
              <FiUser size={18} /> Back to Dashboard
            </button>
          ) : isAdminPage ? (
            <button onClick={() => { navigate('/dashboard'); setProfilePanelOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '15px', fontWeight: 500, color: 'var(--dark)', fontFamily: 'Inter, sans-serif', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)' }}>
              <FiUser size={18} /> Back to Dashboard
            </button>
          ) : (
            <>
              <button onClick={() => { navigate('/dashboard'); setProfilePanelOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '15px', fontWeight: 500, color: 'var(--dark)', fontFamily: 'Inter, sans-serif', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)' }}>
                <FiUser size={18} /> Back to Dashboard
              </button>
              <button onClick={() => { navigate('/dashboard', { state: { openSettings: true } }); setProfilePanelOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '15px', fontWeight: 500, color: 'var(--dark)', fontFamily: 'Inter, sans-serif', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)' }}>
                <FiSettings size={18} /> Settings
              </button>
            </>
          )}
          {isVendorPage && user?.role === 'both' && (
            <Link to="/dashboard" onClick={() => setProfilePanelOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '15px', fontWeight: 500, color: '#E3425C', fontFamily: 'Inter, sans-serif', textDecoration: 'none', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)' }}>
              <FiUser size={18} /> Host Dashboard
            </Link>
          )}
          {!isVendorPage && isVendor && (
            <Link to="/vendor-dashboard" onClick={() => setProfilePanelOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '15px', fontWeight: 500, color: '#d4a853', fontFamily: 'Inter, sans-serif', textDecoration: 'none', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)' }}>
              <FiHome size={18} /> Vendor Dashboard
            </Link>
          )}
          {!isVendorPage && isAdmin && (
            <Link to="/admin" onClick={() => setProfilePanelOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '15px', fontWeight: 500, color: 'var(--dark)', fontFamily: 'Inter, sans-serif', textDecoration: 'none', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)' }}>
              <FiSettings size={18} /> Admin Panel
            </Link>
          )}
          {!isVendorPage && isAdmin && (
            <Link to="/admin?tab=invoices" onClick={() => setProfilePanelOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '15px', fontWeight: 500, color: '#E3425C', fontFamily: 'Inter, sans-serif', textDecoration: 'none', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)' }}>
              <FiFileText size={18} /> Invoice CRM
            </Link>
          )}
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--gray)', padding: '12px 4px 4px' }}>Legal & Info</div>
          <Link to="/terms" onClick={() => setProfilePanelOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', fontWeight: 400, color: 'var(--gray)', fontFamily: 'Inter, sans-serif', textDecoration: 'none' }}>
            <FiFileText size={16} /> Terms & Conditions
          </Link>
          <Link to="/privacy" onClick={() => setProfilePanelOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', fontWeight: 400, color: 'var(--gray)', fontFamily: 'Inter, sans-serif', textDecoration: 'none' }}>
            <FiShield size={16} /> Privacy Policy
          </Link>
          <Link to="/partnerships" onClick={() => setProfilePanelOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', fontWeight: 400, color: 'var(--gray)', fontFamily: 'Inter, sans-serif', textDecoration: 'none', borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)' }}>
            <FiZap size={16} /> Sponsorship
          </Link>
          <button onClick={() => { handleLogout(); setProfilePanelOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 4px', width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '15px', fontWeight: 500, color: 'var(--red)', fontFamily: 'Inter, sans-serif', marginTop: '8px' }}>
            <FiLogOut size={18} /> Logout
          </button>
        </div>
      </div>
    </>
  );

  // ═══════════════════════════════════════════
  // ── DESKTOP LAYOUT ──
  // ═══════════════════════════════════════════
  if (!isMobile) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--cream)' }}>
        {renderSidebar()}
        <main style={{ flex: 1, marginLeft: '250px', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <div key={location.pathname + location.search} className="page-enter" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {children}
          </div>
        </main>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // ── MOBILE LAYOUT ──
  // ═══════════════════════════════════════════
  // ⚠️ position:fixed elements MUST be portaled to document.body.
  //    The container div below uses transform (for the drawer-open slide),
  //    which creates a new CSS containing block — making all fixed children
  //    relative to the scrolled page instead of the viewport.
  const portalRoot = document.body;

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--cream)',
      transform: drawerOpen ? 'translateX(16px)' : 'none',
      transition: 'transform 320ms cubic-bezier(0.16,1,0.3,1)',
    }}>
      {createPortal(renderDrawerHandle(), portalRoot)}
      {drawerOpen && createPortal((
        <div
          onClick={closeDrawer}
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.25)',
          }}
        />
      ), portalRoot)}
      {/* Toast notification */}
      {toast && createPortal((
        <div style={{
          position: 'fixed', top: '80px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, animation: 'slideDown 0.3s cubic-bezier(0.16,1,0.3,1)',
          background: 'var(--dark)', color: '#fff', borderRadius: '14px',
          padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxWidth: '420px', width: 'calc(100% - 32px)',
          fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: '1.5',
        }}>
          <span style={{ flex: 1 }}>{toast.message}</span>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button
              onClick={() => setToast(null)}
              style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                borderRadius: '8px', padding: '6px 10px', cursor: 'pointer',
                fontSize: '13px', fontFamily: 'Inter, sans-serif',
              }}
            >Dismiss</button>
            {toast.action && (
              <button
                onClick={() => { toast.action.onClick(); setToast(null); }}
                style={{
                  background: 'var(--rose)', border: 'none', color: '#fff',
                  borderRadius: '8px', padding: '6px 14px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif',
                  whiteSpace: 'nowrap',
                }}
              >{toast.action.label}</button>
            )}
          </div>
        </div>
      ), portalRoot)}
      {createPortal(renderDrawer(), portalRoot)}
      {notifToast && createPortal(
        <div onClick={() => { if (notifToast.link && !notifToast.link.startsWith('/')) { window.open(notifToast.link, '_blank'); } else { window.dispatchEvent(new CustomEvent('open-notifications')); } setNotifToast(null); }}
          style={{ position: 'fixed', top: '16px', left: '16px', right: '16px', zIndex: 2000, background: '#1C1C1E', color: '#fff', borderRadius: '14px', padding: '14px 16px', boxShadow: '0 10px 30px rgba(0,0,0,0.35)', cursor: 'pointer', fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', gap: '12px', animation: 'modalSlideUp 0.3s ease' }}>
          <div style={{ fontSize: '22px', flexShrink: 0 }}>🔔</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '2px' }}>{notifToast.title}</div>
            <div style={{ fontSize: '12px', opacity: 0.85, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{notifToast.message}</div>
          </div>
          <div onClick={(e) => { e.stopPropagation(); setNotifToast(null); }} style={{ fontSize: '16px', opacity: 0.6, padding: '4px' }}>✕</div>
        </div>,
        portalRoot
      )}
      {renderMobileHeader()}
      {renderProfilePanel()}
      {renderBottomTabs()}
      <main style={{ minHeight: '100vh', paddingBottom: 'calc(64px + env(safe-area-inset-bottom, 24px))' }}>
        <BetaBanner />
        <div key={location.pathname + location.search} className="page-enter" style={{ minHeight: '100vh' }}>
          {children}
        </div>
      </main>
    </div>
  );
}

// ── Tab Button (for bottom tab bar) ──
function TabButton({ icon, label, active, onClick, isDark }) {
  return (
    <button
      className="tab-btn"
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
        padding: '6px 12px', border: 'none', background: 'transparent',
        cursor: 'pointer', color: active ? 'var(--rose)' : 'var(--gray)',
        fontSize: '10px', fontWeight: active ? 600 : 400,
        fontFamily: 'Inter, sans-serif', transition: 'color 0.2s',
        minWidth: '56px',
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
